/**
 * Column Registry for the PNL Grid expanded view.
 *
 * Each column definition declares how to compute and render its value.
 * To add a new column, add an entry to DEFAULT_COLUMNS — no changes
 * to any row component are needed.
 */

import type { MonthlyExpandedValues } from '../../types';

// ── Types ──────────────────────────────────────────────────────────

/** Context passed to each column's getValue function */
export interface ColumnRenderContext {
  /** Raw expanded data for this month (may be null if no data available) */
  expanded: MonthlyExpandedValues | ExpandedTotals | null;
  /** The monthlyValues[month] value (forecast/current) */
  value: number;
  /** Whether this is an expense row (affects sign handling) */
  isExpense: boolean;
  /** 0-based month index */
  monthIndex: number;
  /** Occupied rooms for this month (actual) — used for POR calculations */
  actualOccupiedRooms?: number;
  /** Occupied rooms for this month (forecast) — used for POR calculations */
  forecastOccupiedRooms?: number;
  /** Total actual revenue for this month (for % of Revenue) */
  actualTotalRevenue?: number;
  /** Total budget revenue for this month (for Budgeted % of Rev) */
  budgetTotalRevenue?: number;
  lyBudgetTotalRevenue?: number;
  /** Total forecast revenue for this month (for Forecasted % Rev) */
  forecastTotalRevenue?: number;
  /** ISO-4217 display currency for currency-valued columns */
  currency?: string | null;
}

/** Subset of MonthlyExpandedValues used by section/department/net income totals */
export interface ExpandedTotals {
  forecast: number;
  budget: number;
  actual: number;
  lyActual: number;
  lyBudget?: number;
  lyBudgetOccupiedRooms?: number;
}

export type ColumnColorMode = 'static' | 'variance' | 'sign';

export interface ExpandedColumnDef {
  /** Unique key, e.g. 'actual', 'forecast', 'var' */
  key: string;
  /** Header label, e.g. 'Actual', 'Forecast', 'Var' */
  label: string;
  /** 'data' columns pull raw values; 'computed' columns derive from others */
  category: 'data' | 'computed';
  /** Compute the numeric value for this column */
  getValue: (ctx: ColumnRenderContext) => number;
  /** How to color the cell:
   *  - 'static': always text-slate-700/600/800
   *  - 'variance': green if >= 0, red if < 0
   *  - 'sign': green if >= 0, red if < 0 (used for net income data cols)
   */
  colorMode: ColumnColorMode;
  /** Whether this column supports inline editing (e.g. Forecast) */
  isEditable?: boolean;
  /** Whether this column supports click-to-drill (e.g. Actual, LY for GL detail) */
  isClickable?: boolean;
  /** Click type string passed to onGLDetailClick */
  clickType?: string;
  /** Keys of columns this computed column depends on */
  dependsOn?: string[];
  /** Whether visible by default */
  defaultVisible: boolean;
  /** Default sort order (0-based) */
  defaultOrder: number;
  /** Custom formatter for display. Defaults to formatCurrency if not provided. */
  formatValue?: (value: number, currency?: string | null) => string;
}

// ── Helpers ────────────────────────────────────────────────────────

/** Normalize actual value: flip sign for revenue (stored negative), keep for expense */
function normalizeActual(raw: number, isExpense: boolean): number {
  return isExpense ? raw : raw * -1;
}

/** Normalize LY actual: same logic as actual */
function normalizeLyActual(raw: number, isExpense: boolean): number {
  return isExpense ? raw : raw * -1;
}

/** Normalize budget for display: expense budgets stored negative, flip for display */
export function displayBudget(budget: number, isExpense: boolean): number {
  return isExpense ? -budget : budget;
}

/** Format a POR value with 1 decimal and $ sign */
export function formatPor(value: number, currency?: string | null): string {
  const abs = Math.abs(value);
  const formatted = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(abs);
  return value < 0 ? `-${formatted}` : formatted;
}

/** Format a percentage with 1 decimal */
export function formatPercent(value: number): string {
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return value < 0 ? `-${formatted}%` : `${formatted}%`;
}

// ── Column Definitions ─────────────────────────────────────────────

export const DEFAULT_COLUMNS: ExpandedColumnDef[] = [
  {
    key: 'actual',
    label: 'Actual',
    category: 'data',
    getValue: (ctx) => {
      const raw = ctx.expanded?.actual || 0;
      return normalizeActual(raw, ctx.isExpense);
    },
    colorMode: 'static',
    isClickable: true,
    clickType: 'Actual',
    defaultVisible: true,
    defaultOrder: 0,
  },
  {
    key: 'forecast',
    label: 'Forecast',
    category: 'data',
    getValue: (ctx) => {
      const forecast = ctx.expanded?.forecast ?? ctx.value;
      return forecast;
    },
    colorMode: 'static',
    isEditable: true,
    defaultVisible: true,
    defaultOrder: 1,
  },
  {
    key: 'var',
    label: 'Act vs Fcst',
    category: 'computed',
    getValue: (ctx) => {
      const raw = ctx.expanded?.actual || 0;
      const actual = normalizeActual(raw, ctx.isExpense);
      const forecast = ctx.expanded?.forecast ?? ctx.value;
      return ctx.isExpense ? forecast - actual : actual - forecast;
    },
    colorMode: 'variance',
    dependsOn: ['actual', 'forecast'],
    defaultVisible: true,
    defaultOrder: 2,
  },
  {
    key: 'budget',
    label: 'Budget',
    category: 'data',
    getValue: (ctx) => {
      const budget = ctx.expanded?.budget || 0;
      return displayBudget(budget, ctx.isExpense);
    },
    colorMode: 'static',
    defaultVisible: true,
    defaultOrder: 3,
  },
  {
    key: 'vsBud',
    label: 'Act vs Bud',
    category: 'computed',
    getValue: (ctx) => {
      const raw = ctx.expanded?.actual || 0;
      const actual = normalizeActual(raw, ctx.isExpense);
      const budget = ctx.expanded?.budget || 0;
      return ctx.isExpense ? (budget * -1) - actual : actual - budget;
    },
    colorMode: 'variance',
    dependsOn: ['actual', 'budget'],
    defaultVisible: true,
    defaultOrder: 4,
  },
  {
    key: 'budVsFcst',
    label: 'Bud vs Fcst',
    category: 'computed',
    getValue: (ctx) => {
      const forecast = ctx.expanded?.forecast ?? ctx.value;
      const budget = ctx.expanded?.budget || 0;
      const displayForecast = forecast;
      const displayBud = displayBudget(budget, ctx.isExpense);
      return displayForecast - displayBud;
    },
    colorMode: 'variance',
    dependsOn: ['forecast', 'budget'],
    defaultVisible: true,
    defaultOrder: 5,
  },
  {
    key: 'ly',
    label: 'LY',
    category: 'data',
    getValue: (ctx) => {
      const raw = ctx.expanded?.lyActual || 0;
      return normalizeLyActual(raw, ctx.isExpense);
    },
    colorMode: 'static',
    isClickable: true,
    clickType: 'LY',
    defaultVisible: true,
    defaultOrder: 6,
  },
  {
    key: 'vsLy',
    label: 'Act vs LY',
    category: 'computed',
    getValue: (ctx) => {
      const raw = ctx.expanded?.actual || 0;
      const actual = normalizeActual(raw, ctx.isExpense);
      const rawLy = ctx.expanded?.lyActual || 0;
      const lyActual = normalizeLyActual(rawLy, ctx.isExpense);
      return ctx.isExpense ? lyActual - actual : actual - lyActual;
    },
    colorMode: 'variance',
    dependsOn: ['actual', 'ly'],
    defaultVisible: true,
    defaultOrder: 7,
  },
  {
    key: 'por',
    label: '$POR',
    category: 'data',
    getValue: (ctx) => {
      const raw = ctx.expanded?.actual || 0;
      const actual = normalizeActual(raw, ctx.isExpense);
      const rooms = ctx.actualOccupiedRooms || 0;
      return rooms > 0 ? actual / rooms : 0;
    },
    colorMode: 'static',
    formatValue: formatPor,
    defaultVisible: false,
    defaultOrder: 8,
  },
  {
    key: 'fcstPor',
    label: 'Fcst $POR',
    category: 'data',
    getValue: (ctx) => {
      const forecast = ctx.expanded?.forecast ?? ctx.value;
      const displayForecast = forecast;
      const rooms = ctx.forecastOccupiedRooms || 0;
      return rooms > 0 ? displayForecast / rooms : 0;
    },
    colorMode: 'static',
    formatValue: formatPor,
    defaultVisible: false,
    defaultOrder: 9,
  },
  {
    key: 'budPor',
    label: 'Bud $POR',
    category: 'data',
    getValue: (ctx) => {
      const budget = ctx.expanded?.budget || 0;
      const displayBud = displayBudget(budget, ctx.isExpense);
      const rooms = ctx.forecastOccupiedRooms || 0;
      return rooms > 0 ? displayBud / rooms : 0;
    },
    colorMode: 'static',
    formatValue: formatPor,
    defaultVisible: false,
    defaultOrder: 10,
  },
  {
    key: 'porVar',
    label: 'POR Act vs Fcst',
    category: 'computed',
    getValue: (ctx) => {
      const raw = ctx.expanded?.actual || 0;
      const actual = normalizeActual(raw, ctx.isExpense);
      const actualRooms = ctx.actualOccupiedRooms || 0;
      const actualPor = actualRooms > 0 ? actual / actualRooms : 0;

      const forecast = ctx.expanded?.forecast ?? ctx.value;
      const displayForecast = forecast;
      const fcstRooms = ctx.forecastOccupiedRooms || 0;
      const fcstPor = fcstRooms > 0 ? displayForecast / fcstRooms : 0;

      return actualPor - fcstPor;
    },
    colorMode: 'variance',
    formatValue: formatPor,
    dependsOn: ['por', 'fcstPor'],
    defaultVisible: false,
    defaultOrder: 11,
  },
  {
    key: 'pctRev',
    label: '% Rev',
    category: 'computed',
    getValue: (ctx) => {
      const raw = ctx.expanded?.actual || 0;
      const actual = normalizeActual(raw, ctx.isExpense);
      const rev = ctx.actualTotalRevenue || 0;
      return rev > 0 ? (actual / rev) * 100 : 0;
    },
    colorMode: 'static',
    formatValue: formatPercent,
    defaultVisible: false,
    defaultOrder: 12,
  },
  {
    key: 'budPctRev',
    label: 'Bud % Rev',
    category: 'computed',
    getValue: (ctx) => {
      const budget = ctx.expanded?.budget || 0;
      const displayBud = displayBudget(budget, ctx.isExpense);
      const rev = ctx.budgetTotalRevenue || 0;
      return rev > 0 ? (displayBud / rev) * 100 : 0;
    },
    colorMode: 'static',
    formatValue: formatPercent,
    defaultVisible: false,
    defaultOrder: 13,
  },
  {
    key: 'fcstPctRev',
    label: 'Fcst % Rev',
    category: 'computed',
    getValue: (ctx) => {
      const forecast = ctx.expanded?.forecast ?? ctx.value;
      const displayForecast = forecast;
      const rev = ctx.forecastTotalRevenue || 0;
      return rev > 0 ? (displayForecast / rev) * 100 : 0;
    },
    colorMode: 'static',
    formatValue: formatPercent,
    defaultVisible: false,
    defaultOrder: 14,
  },
];

/** Keys remain stable so saved forecast layouts and editing targets keep working. */
export function getColumnsForVersion(isBudget = false): ExpandedColumnDef[] {
  if (!isBudget) return DEFAULT_COLUMNS;
  const labels: Record<string, string> = {
    forecast: 'Budget', var: 'Act vs Budget', budget: 'Published Budget',
    vsBud: 'Act vs Published Budget', budVsFcst: 'Budget vs Published',
    ly: 'LY Actual', fcstPor: 'Budget $POR', budPor: 'Published Budget $POR',
    porVar: 'POR Act vs Budget', budPctRev: 'Published Budget % Rev', fcstPctRev: 'Budget % Rev',
  };
  return [
    ...DEFAULT_COLUMNS.map(c => ({ ...c, label: labels[c.key] || c.label,
      defaultVisible: ['forecast', 'ly', 'lyBudget'].includes(c.key) })),
    { key: 'lyBudget', label: 'LY Budget', category: 'data', colorMode: 'static',
      defaultVisible: true, defaultOrder: 15,
      getValue: ctx => displayBudget(ctx.expanded?.lyBudget || 0, ctx.isExpense) },
    { key: 'lyBudgetPor', label: 'LY Budget $POR', category: 'computed', colorMode: 'static',
      defaultVisible: false, defaultOrder: 16, formatValue: formatPor,
      getValue: ctx => {
        const rooms = ctx.expanded?.lyBudgetOccupiedRooms || 0;
        return rooms > 0 ? displayBudget(ctx.expanded?.lyBudget || 0, ctx.isExpense) / rooms : 0;
      } },
    { key: 'lyBudgetPctRev', label: 'LY Budget % Rev', category: 'computed', colorMode: 'static',
      defaultVisible: false, defaultOrder: 18, formatValue: formatPercent,
      getValue: ctx => (ctx.lyBudgetTotalRevenue || 0) > 0
        ? displayBudget(ctx.expanded?.lyBudget || 0, ctx.isExpense) / ctx.lyBudgetTotalRevenue! * 100 : 0 },
    { key: 'budgetVsLyBudget', label: 'Budget vs LY Budget', category: 'computed', colorMode: 'variance',
      defaultVisible: true, defaultOrder: 17,
      getValue: ctx => {
        const plan = ctx.expanded?.forecast ?? ctx.value;
        const prior = displayBudget(ctx.expanded?.lyBudget || 0, ctx.isExpense);
        return ctx.isExpense ? prior - plan : plan - prior;
      } },
  ];
}

// ── Net Income Column Overrides ────────────────────────────────────
// Net Income computes values differently (rev - exp), so it uses
// separate getValue functions. These are the same columns but with
// net income-specific computations.

export interface NetIncomeContext {
  revExpanded: ExpandedTotals | null;
  expExpanded: ExpandedTotals | null;
}

/** Compute net income values for a given column key */
export function getNetIncomeValue(key: string, rev: ExpandedTotals | null, exp: ExpandedTotals | null): number {
  const netActual = ((rev?.actual || 0) * -1) - (exp?.actual || 0);
  const netForecast = (rev?.forecast || 0) - (exp?.forecast || 0);
  const netBudget = (rev?.budget || 0) - ((exp?.budget || 0) * -1);
  const netLyActual = ((rev?.lyActual || 0) * -1) - (exp?.lyActual || 0);

  switch (key) {
    case 'lyBudget': return (rev?.lyBudget || 0) + (exp?.lyBudget || 0);
    case 'budgetVsLyBudget': return netForecast - ((rev?.lyBudget || 0) + (exp?.lyBudget || 0));
    case 'lyBudgetPctRev': return 0;
    case 'lyBudgetPor': return 0;
    case 'actual': return netActual;
    case 'forecast': return netForecast;
    case 'var': return netActual - netForecast;
    case 'budget': return netBudget;
    case 'vsBud': return netActual - netBudget;
    case 'budVsFcst': return netForecast - netBudget;
    case 'ly': return netLyActual;
    case 'vsLy': return netActual - netLyActual;
    case 'por': return 0;
    case 'fcstPor': return 0;
    case 'budPor': return 0;
    case 'porVar': return 0;
    case 'pctRev': return 0;
    case 'budPctRev': return 0;
    case 'fcstPctRev': return 0;
    default: return 0;
  }
}

/** Get color mode for net income column - data cols use 'sign', computed use 'variance' */
export function getNetIncomeColorMode(col: ExpandedColumnDef): ColumnColorMode {
  return col.category === 'data' ? 'sign' : 'variance';
}

// ── Total column ───────────────────────────────────────────────────

/** Index the Total column occupies in the expanded-column state, after the 12 months. */
export const TOTAL_COLUMN_INDEX = 12;

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/**
 * Year totals of a row's per-month expanded values, in the same stored sign
 * conventions as any single month, so the column definitions price the year
 * exactly as they price a month.
 */
export function sumExpandedYear(byMonth: Record<string, MonthlyExpandedValues | ExpandedTotals> | undefined | null): ExpandedTotals {
  const year: ExpandedTotals = { forecast: 0, budget: 0, actual: 0, lyActual: 0, lyBudget: 0, lyBudgetOccupiedRooms: 0 };
  if (!byMonth) return year;
  for (const m of MONTH_KEYS) {
    const x = byMonth[m];
    if (!x) continue;
    year.forecast += x.forecast || 0;
    year.budget += x.budget || 0;
    year.actual += x.actual || 0;
    year.lyActual += x.lyActual || 0;
    year.lyBudget! += x.lyBudget || 0;
    year.lyBudgetOccupiedRooms! += x.lyBudgetOccupiedRooms || 0;
  }
  return year;
}

export interface TotalContextInputs {
  expandedByMonth: Record<string, MonthlyExpandedValues | ExpandedTotals> | undefined | null;
  /** The row's year total as displayed (the collapsed Total cell). */
  value: number;
  isExpense: boolean;
  occupiedRoomsPerMonth?: Record<string, { actual: number; forecast: number }>;
  revenuePerMonth?: Record<string, ExpandedTotals>;
  currency?: string | null;
}

/**
 * Render context for the expanded Total column: the row's year sums with
 * year-summed room nights and revenue as denominators, so $POR and % Rev on
 * the Total read as true annual ratios rather than sums of monthly ratios.
 */
export function buildTotalContext({ expandedByMonth, value, isExpense, occupiedRoomsPerMonth, revenuePerMonth, currency }: TotalContextInputs): ColumnRenderContext {
  let actualRooms = 0;
  let forecastRooms = 0;
  let actualRev = 0;
  let budgetRev = 0;
  let lyBudgetRev = 0;
  let forecastRev = 0;
  for (const m of MONTH_KEYS) {
    const rooms = occupiedRoomsPerMonth?.[m];
    actualRooms += rooms?.actual || 0;
    forecastRooms += rooms?.forecast || 0;
    const rev = revenuePerMonth?.[m];
    actualRev += rev?.actual ? rev.actual * -1 : 0;
    budgetRev += rev?.budget || 0;
    lyBudgetRev += rev?.lyBudget || 0;
    forecastRev += rev?.forecast || 0;
  }
  return {
    expanded: sumExpandedYear(expandedByMonth),
    value,
    isExpense,
    monthIndex: TOTAL_COLUMN_INDEX,
    actualOccupiedRooms: actualRooms,
    forecastOccupiedRooms: forecastRooms,
    actualTotalRevenue: actualRev,
    budgetTotalRevenue: budgetRev,
    lyBudgetTotalRevenue: lyBudgetRev,
    forecastTotalRevenue: forecastRev,
    currency,
  };
}

// ── Utilities ──────────────────────────────────────────────────────

/** Get the default visible columns in default order */
export function getDefaultVisibleKeys(): string[] {
  return DEFAULT_COLUMNS
    .filter(c => c.defaultVisible)
    .sort((a, b) => a.defaultOrder - b.defaultOrder)
    .map(c => c.key);
}

/** Look up a column definition by key */
export function getColumnByKey(key: string): ExpandedColumnDef | undefined {
  return DEFAULT_COLUMNS.find(c => c.key === key);
}

/** Get ordered active columns from a list of visible keys */
export function getActiveColumns(visibleKeys: string[], isBudget = false): ExpandedColumnDef[] {
  const columns = getColumnsForVersion(isBudget);
  return visibleKeys
    .map(key => columns.find(c => c.key === key))
    .filter((c): c is ExpandedColumnDef => c !== undefined);
}
